import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Box,
  Button,
  Typography,
  TextField,
  CircularProgress,
  Card,
  CardMedia,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { UploadFileToAzureDB } from "../../components/Azure";
import apiPrakasak from "../../api/apiPrakasak";

const PerformanceCertificate = () => {
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(""); // API certificate preview URL
  const [filePreviewUrl, setFilePreviewUrl] = useState(""); // File preview URL
  const [isUploading, setIsUploading] = useState(false);
  const [isApiCall, setIsApiCall] = useState(false);
  const [message, setMessage] = useState("");
  const [certificateType, setCertificateType] = useState("top_parents"); // Default type
  const [types, setTypes] = useState([
    { value: "top_parents", label: "Top Parents" },
    // { value: "top_students", label: "Top Students" },
    // { value: "top_teachers", label: "Top Teachers" },
  ]);

  // Fetch the image from the backend based on the selected certificate type
  useEffect(() => {
    const fetchCertificate = async () => {
      try {
        const response = await apiPrakasak.get(
          `/getTopPerfCertFormat/${certificateType}`
        );
        if (response?.data?.certFormatUrl) {
          setPreviewUrl(response.data.certFormatUrl);
          setMessage("");
        } else {
          setPreviewUrl("");
          setMessage("No certificate found for this category.");
        }
      } catch (error) {
        console.error("Error fetching certificate:", error);
        setMessage("Failed to fetch the certificate.");
      }
    };

    fetchCertificate();
  }, [certificateType]);

  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    setFile(selectedFile);
    setFilePreviewUrl(URL.createObjectURL(selectedFile)); // Set file preview URL
  };

  const generateFileName = (originalName) => {
    const extension = originalName.split(".").pop();
    return `${certificateType}_${Date.now()}.${extension}`;
  };

  const handleUpload = async () => {
    if (!file) {
      setMessage("Please select a file before uploading.");
      return;
    }

    setMessage("");
    setIsUploading(true);

    try {
      const generatedName = generateFileName(file.name);
      const fileUrl = await UploadFileToAzureDB(file, generatedName);

      setIsUploading(false);

      if (!fileUrl?.url) {
        setMessage("Failed to generate the file URL.");
        return;
      }

      setIsApiCall(true);
      const payload = {
        topPerfType: certificateType,
        certFormatUrl: fileUrl?.url,
      };
      const response = await apiPrakasak.post(
        "uploadTopPerfCertFormat",
        payload
      );

      setIsApiCall(false);

      if (response?.status === 200) {
        setMessage("File uploaded successfully!");
        setPreviewUrl(fileUrl?.url); // Update the preview with the newly uploaded image
        setTypes([{ value: "top_parents", label: "Top Parents" }]);
        setFilePreviewUrl("");
        setFile("");
      } else {
        setMessage("Failed to upload the file.");
      }
    } catch (error) {
      setIsUploading(false);
      setIsApiCall(false);
      console.error("Error uploading the file:", error);
      setMessage("An error occurred while uploading the file.");
    }
  };

  const handleTypeChange = (event) => {
    setCertificateType(event.target.value);
  };

  return (
    <Box
      sx={{
        maxWidth: "1000px",
        margin: "50px auto",
        padding: "40px",
        borderRadius: "16px",
        boxShadow: "0 10px 25px rgba(0,0,0,0.2)",
        textAlign: "center",
        background: "linear-gradient(145deg, #ffffff, #e6e6e6)",
        fontFamily: "'Poppins', sans-serif",
        position: "relative",
      }}
    >
      <Typography
        variant="h5"
        component="h1"
        sx={{
          marginBottom: "20px",
          color: "#333",
          fontWeight: 700,
          textTransform: "uppercase",
          letterSpacing: "1px",
        }}
      >
        Upload Performance Certificate
      </Typography>

      <Grid container spacing={3}>
        {/* Left Section: Certificate Type Dropdown */}
        <Grid item xs={12} sm={6}>
          <FormControl
            fullWidth
            sx={{
              marginBottom: "25px",
              "& .MuiInputLabel-root": { color: "#007bff" },
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  borderColor: "#007bff",
                },
                "&:hover fieldset": {
                  borderColor: "#0056b3",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#0056b3",
                },
              },
            }}
          >
            <InputLabel id="certificate-type-label">
              Certificate Type
            </InputLabel>
            <Select
              labelId="certificate-type-label"
              value={certificateType}
              onChange={handleTypeChange}
            >
              {types.map((type) => (
                <MenuItem key={type.value} value={type.value}>
                  {type.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* API-based Certificate Preview */}
          <Card
            sx={{
              marginBottom: "20px",
              boxShadow: "0 8px 20px rgba(0,0,0,0.1)",
              borderRadius: "12px",
              overflow: "hidden",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              backgroundColor: "#f9f9f9",
            }}
          >
            {previewUrl ? (
              <CardMedia
                component="img"
                height="220"
                image={previewUrl}
                alt="Certificate Preview"
                sx={{
                  objectFit: "cover",
                  // width: "100%", // Ensure it stretches within the Card
                  // maxHeight: "220px", // Maintain height boundaries
                }}
              />
            ) : (
              <Typography
                sx={{
                  padding: "20px",
                  color: "#888",
                  fontSize: "16px",
                  fontWeight: 500,
                }}
              >
                No preview available
              </Typography>
            )}
          </Card>
        </Grid>

        {/* Right Section: File Upload */}
        <Grid item xs={12} sm={6}>
          <Button
            variant="contained"
            component="label"
            startIcon={<CloudUploadIcon />}
            sx={{
              marginBottom: "20px",
              padding: "10px 25px",
              background: "linear-gradient(145deg, #007bff, #0056b3)",
              color: "#fff",
              fontSize: "16px",
              fontWeight: 600,
              borderRadius: "8px",
              transition: "all 0.3s ease-in-out",
              "&:hover": {
                background: "linear-gradient(145deg, #0056b3, #003c7f)",
                transform: "scale(1.05)",
              },
            }}
          >
            Choose File
            <input
              type="file"
              hidden
              accept=".jpg,.jpeg,.png"
              onChange={handleFileChange}
            />
          </Button>

          {file && (
            <TextField
              value={file.name}
              variant="outlined"
              disabled
              fullWidth
              sx={{
                marginBottom: "25px",
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    borderColor: "#ddd",
                  },
                  "&:hover fieldset": {
                    borderColor: "#ccc",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#aaa",
                  },
                },
              }}
            />
          )}

          {/* File Preview */}
          {file && filePreviewUrl && (
            <Card
              sx={{
                marginBottom: "20px",
                boxShadow: "0 8px 20px rgba(0,0,0,0.1)",
                borderRadius: "12px",
                overflow: "hidden",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                backgroundColor: "#f9f9f9",
              }}
            >
              <CardMedia
                component="img"
                height="220"
                image={filePreviewUrl}
                alt="Uploaded Image Preview"
                sx={{
                  objectFit: "cover",
                  // width: "100%", // Ensure it stretches within the Card
                  // maxHeight: "220px", // Maintain height boundaries
                }}
              />
            </Card>
          )}

          {/* Upload Button */}
          <Button
            variant="contained"
            color="primary"
            onClick={handleUpload}
            disabled={isUploading || !file}
            sx={{
              padding: "12px 30px",
              fontSize: "16px",
              fontWeight: 600,
              borderRadius: "8px",
              width: "100%",
              background: isUploading ? "#999" : "#007bff",
              "&:hover": {
                background: isUploading ? "#999" : "#0056b3",
              },
            }}
          >
            {isUploading ? <CircularProgress size={24} /> : "Upload"}
          </Button>

          {message && (
            <Typography
              variant="body2"
              sx={{
                marginTop: "20px",
                color: isUploading ? "orange" : "green",
                fontWeight: 600,
              }}
            >
              {message}
            </Typography>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default PerformanceCertificate;
